import { segmentSentences, sentenceBounds } from './sentences.js';

/**
 * packages/ui/answerParagraphs.mjs — RUN SH · SH-2. ONE COPY OF ONE RULE.
 *
 * ═══════════════════════════════════════════════════════════════════════════════════════════════
 * 🔴 WHY THIS FILE EXISTS. This function was module-private in TWO apps — Clark's
 * `apps/main/client/src/screens/Clark/answer/ClarkAnswerV2.jsx` and Research's
 * `apps/steel/client/src/components/AnswerV2.jsx` — and both copies said so in their own headers.
 * RUN RA wrote it for Clark; RUN RC adopted it into Research and recorded the drift risk rather
 * than hiding it. Neither run could remove the duplication, because neither owned both apps.
 *
 * This is the shape the codebase already uses for exactly this problem: `@citeclerk/ui/howWeKnow`
 * is plain data imported by both apps *precisely so two copies of one rule cannot disagree*.
 *
 * ⭐ THEY HAD NOT DISAGREED YET, AND THAT WAS MEASURED, NOT ASSUMED. Before the swap, both
 * implementations were sliced out of their files on disk and run over 174 inputs — 131 archived
 * prose strings (112 research-route answers + 19 Clark answers), 19 real `segments` arrays, and 24
 * adversarial inputs that drive every branch. **174 of 174 identical.** The two texts differed by
 * four bytes of quote style and indentation and by nothing else.
 * `apps/main/ops/run-sh/out/agree-before.json`.
 *
 * ── THE RULE, AND THE TWO THINGS IT IS CAREFUL ABOUT ──────────────────────────────────────────
 *
 * 🔴 ONLY `text` SEGMENTS SPLIT. `unresolved` / `unchecked` / `unsupported` / `casename` are INLINE
 * MARKERS that sit inside a sentence — breaking a paragraph at one would tear the sentence carrying
 * it in half. An unknown future type falls here too and stays inline, which is the same
 * conservative default both apps' render switches take for one.
 *
 * ⚠️ SPLIT ON `\n{2,}`, NOT ON `\n`. Recomputed by this run over both archives rather than quoted:
 * of 103 newline runs across the 112 research-route answers **every one is exactly two long**, and
 * of the 6 runs across the 19 Clark answers **every one is exactly two long**. A lone `\n` has never
 * been observed on either route, so treating one as a paragraph boundary would be a guess about a
 * shape nothing has produced.
 *
 * ⭐ AND THE POPULATION IS NOT COSMETIC. 36 of the 112 research-route answers (32.1%) carry a real
 * break — roughly one answer in three — against 2 of 19 on the Clark route. RUN RA's original "0 of
 * 17 on the research route" came from a 36-string sample and is superseded by the full archive.
 *
 * 🔴 WHAT THIS FUNCTION CANNOT FIX. 76 of the 112 render as a single paragraph because the model
 * emitted one unbroken block. That half is COMPOSITION, it belongs to `clarkPipeline.js`'s `SYSTEM`
 * and `ANSWER_SCHEMA`, and it is reported by RA and RC rather than reached for. This function's job
 * is to stop destroying the structure that DOES arrive.
 *
 * @param {Array<{type?: string, text?: string}>} segs
 * @returns {Array<Array<object>>} one array of nodes per paragraph, empty paragraphs dropped
 */
export function toParagraphs(segs) {
  const paras = [[]];
  for (const s of segs) {
    if (s.type && s.type !== 'text') { paras[paras.length - 1].push(s); continue; }
    const parts = String(s.text ?? '').split(/\n{2,}/);
    parts.forEach((part, i) => {
      if (i > 0) paras.push([]);
      // Trim ONLY at an edge where a break actually was. A blanket `.trim()` would eat the single
      // space that separates an inline marker from the words after it.
      const t = parts.length === 1 ? part
        : i === 0 ? part.replace(/\s+$/, '')
          : i === parts.length - 1 ? part.replace(/^\s+/, '')
            : part.trim();
      if (t) paras[paras.length - 1].push({ ...s, type: 'text', text: t });
    });
  }
  return paras.filter((nodes) => nodes.length > 0);
}


/* ══ 🔴🔴 RUN CR · BLOCK 6 — THE LEGACY SENTENCE DROP, IN THE ONE FILE BOTH APPS ALREADY SHARE. ══
 *
 * ADAM'S RULING: **"We should not withhold any quotations. Period."** and **"Fix the answer so it
 * still makes sense. It can't look broken."** — for Clark and Research alike.
 *
 * FRESH ANSWERS ARE ALREADY FIXED, AND NOT HERE. `removeUnsupportedSentences`
 * (apps/steel/server/lib/goodlaw/proseSupport.js) removes the whole sentence server-side before the
 * envelope is written, so a newly composed answer carries no `unsupported` segment at all.
 *
 * 🔴 THIS IS FOR THE ROWS ALREADY IN THE TABLE. Every stored envelope written before tonight still
 * holds `{{unsupported:n}}`, and `/research/answer/:id` and Clark's restored turns re-render them
 * verbatim — a photograph is never re-run. Without this, those answers keep the hole the composer's
 * sentence was built around: *"The Court has also said that … in the qualified-immunity context."*
 * Adam's ruling is about what a reader sees, and a reader sees stored answers.
 *
 * ⭐ SO THE SAME REMEDY, AT THE SAME UNIT, ON THE READ PATH: drop the SENTENCE the failed quotation
 * was the support for. `sentenceBounds` is `packages/ui/sentences.js` — THE one splitter, the same
 * one the server rounds with and the same one the judge's spans were numbered against. RUN BD's
 * finding is why that matters: a second boundary rule in this repo disagreed with the shared one on
 * ~20 % of boundaries and 21 of 52 display spans showed text the judge never cleared. There is no
 * new sentence logic in this file — only a mapping from character offsets back onto segments.
 *
 * ⚠️ THE GUARD, SAME AS THE SERVER'S. If dropping would leave nothing to read, the segments are
 * returned UNCHANGED and the client's own `unsupported` branch renders its quiet ellipsis. A blank
 * answer is the one outcome worse than an awkward sentence.
 *
 * @param {Array<{type?: string, text?: string}>} segs
 * @returns {Array<object>} the same list with each failed quotation's sentence removed
 */
export function dropUnsupportedSentences(segs) {
  const list = Array.isArray(segs) ? segs : [];
  if (!list.some((s) => s && s.type === 'unsupported')) return list;

  /* The reading text, and where each segment sits in it. An `unsupported` segment contributes NO
     characters — its words were kept off the wire — so it is a zero-width position, which is
     exactly what `sentenceBounds` needs to find the sentence carrying it. */
  let at = 0;
  const spans = list.map((s) => {
    const text = s && s.type === 'unsupported' ? '' : String((s && s.text) ?? '');
    const span = { seg: s, start: at, end: at + text.length, text };
    at += text.length;
    return span;
  });
  const flat = spans.map((x) => x.text).join('');
  const sentences = segmentSentences(flat);

  /* Every failed quotation's sentence, as character ranges over `flat`. */
  const cuts = spans
    .filter((x) => x.seg && x.seg.type === 'unsupported')
    .map((x) => sentenceBounds(flat, x.start, x.start, sentences));

  const cut = (i) => cuts.some((c) => i >= c.start && i < c.end);

  const out = [];
  for (const x of spans) {
    if (x.seg && x.seg.type === 'unsupported') {
      // The marker itself is zero-width, so `cut()` cannot speak for it. It always goes.
      continue;
    }
    // Keep only the characters of this segment that no cut covers. A segment can straddle a
    // boundary — the sentence before the failed one ends inside the same `text` run.
    let kept = '';
    for (let i = x.start; i < x.end; i += 1) if (!cut(i)) kept += flat[i];
    if (kept) out.push({ ...x.seg, text: kept });
  }

  const tidied = tidySegments(out);
  if (!tidied.some((s) => String(s.text ?? '').trim())) return list;   // THE GUARD
  return tidied;
}

/**
 * Close the seam an excision leaves.
 *
 * 🔴 THE SEAM IS BETWEEN SEGMENTS, NOT INSIDE ONE, WHICH IS WHY THIS CANNOT BE A `.map`. A removed
 * sentence routinely takes the tail of one `text` run and the head of the next, leaving `"…
 * established. "` followed by `" Since …"` — two segments each holding ONE legal space, and a
 * doubled space on screen. So the walk carries whether the last character emitted was a space and
 * strips a leading one from whatever comes next. `casename` and the other inline markers are
 * carried through untouched but still count as emitted text for that purpose, or the space before
 * an italic case name would be eaten.
 */
function tidySegments(segs) {
  const out = [];
  let endsWithSpace = true;   // true at the start of the answer, so leading space is trimmed
  for (const seg of segs) {
    const s = { ...seg };
    if (s.type && s.type !== 'text') {
      out.push(s);
      endsWithSpace = /\s$/.test(String(s.text ?? ''));
      continue;
    }
    let t = String(s.text ?? '')
      .replace(/[ \t]{2,}/g, ' ')
      .replace(/[ \t]+([.,;:!?])/g, '$1');
    if (endsWithSpace) t = t.replace(/^[ \t]+/, '');
    if (!t) continue;
    endsWithSpace = /[ \t]$/.test(t);
    s.text = t;
    out.push(s);
  }
  // Trailing whitespace at the very end of the answer.
  for (let i = out.length - 1; i >= 0; i -= 1) {
    if (out[i].type && out[i].type !== 'text') break;
    out[i].text = String(out[i].text ?? '').replace(/[ \t]+$/, '');
    if (!out[i].text) { out.splice(i, 1); continue; }
    break;
  }
  return out;
}

export default toParagraphs;
